/**
 * People (slice 6a §6.1, artboards 70, 70b, 75): the crew and the invites
 * out, Invite someone, and each row's ⋯. The owner's: a member is sent
 * home, as from Activity. The phone's rows are two lines, with Resend under
 * an invite.
 */
import { Button } from "@crew/ui/components/button";
import { PersonAvatar } from "@crew/ui/components/crew-avatar";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "@crew/ui/components/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@crew/ui/components/dropdown-menu";
import { BackLink } from "@crew/ui/components/back-link";
import { PageHeader } from "@crew/ui/components/typography";
import { cn } from "@crew/ui/lib/utils";
import { Ellipsis, Mail, Plus } from "lucide-react";
import { useCallback, useState, type ReactNode } from "react";
import { errorHint } from "../../app/api";
import type { Me } from "../../app/me";
import { personTint } from "../../app/tints";
import {
  useCancelInvite,
  usePeople,
  useRemovePerson,
  useResend,
  type Invite,
  type PeopleList,
  type Person,
} from "./api";
import { InviteWindow } from "./invite-window";
import {
  appsLine,
  crewCount,
  invitedBy,
  inviteState,
  lastSeen,
  sentToast,
  type InviteState,
} from "./people-view";
import { Navigate } from "react-router-dom";
import { HOME } from "../../app/home";
import { Toast, type ToastMessage } from "./toast";

const errorToast = (error: unknown): ToastMessage => ({ id: Date.now(), text: errorHint(error) });

const firstName = (name: string) => name.trim().split(/\s+/)[0] || name;

/** The route: People is the owner's; a member goes home. */
export function PeopleRoute({ me }: { me: Me }) {
  if (me.user.role !== "owner") return <Navigate to={HOME} replace />;
  return <PeoplePage me={me} />;
}

export function PeoplePage({ me }: { me: Me }) {
  const owner = me.user.role === "owner";
  const [inviting, setInviting] = useState(false);
  const [removing, setRemoving] = useState<Person | null>(null);
  const [toast, setToast] = useState<ToastMessage | null>(null);
  const closeToast = useCallback(() => setToast(null), []);
  const invite = owner ? () => setInviting(true) : undefined;
  return (
    <main
      data-people-page
      className="flex w-full max-w-6xl flex-col gap-7 px-4 pt-5 pb-16 sm:px-6 md:gap-6 md:px-12 md:pt-9"
    >
      <BackLink href="/settings">Settings</BackLink>
      <PageHeader
        title="People"
        description="Everyone here talks to the same Chief of Staff, and sees every chat."
        actions={invite ? <InviteButton onClick={invite} wide /> : undefined}
      />
      <Crew me={me} onInvite={invite} onRemove={setRemoving} onToast={setToast} />
      <HowItWorks />
      {owner ? (
        <InviteWindow
          open={inviting}
          onOpenChange={setInviting}
          workspace={me.workspace.name}
          onSent={(out) => setToast(sentToast(out))}
        />
      ) : null}
      {removing ? <RemoveDialog person={removing} onClose={() => setRemoving(null)} /> : null}
      {toast ? <Toast key={toast.id} message={toast} onClose={closeToast} /> : null}
    </main>
  );
}

/** Invite someone: in the page's header on desktop, beside "In the crew" on the phone. */
function InviteButton({ onClick, wide = false }: { onClick: () => void; wide?: boolean }) {
  if (wide) {
    return (
      <Button size="lg" className="hidden gap-2 px-3.5 md:inline-flex" onClick={onClick}>
        <Plus />
        Invite someone
      </Button>
    );
  }
  return (
    <Button className="h-9 gap-1.5 px-3 md:hidden" onClick={onClick}>
      <Plus />
      Invite
    </Button>
  );
}

/** In the crew: its count, the people and the invites, and what members can do. */
function Crew({
  me,
  onInvite,
  onRemove,
  onToast,
}: {
  me: Me;
  /** The owner's; a member has none. */
  onInvite?: () => void;
  onRemove: (person: Person) => void;
  onToast: (toast: ToastMessage) => void;
}) {
  const people = usePeople();
  const list = people.data;
  return (
    <section aria-label="In the crew" className="flex flex-col gap-3">
      <div className="flex items-center justify-between md:items-baseline md:justify-start md:gap-2.5">
        <div className="flex flex-col gap-0.5 md:flex-row md:items-baseline md:gap-2.5">
          <h2 className="text-question font-semibold text-foreground">In the crew</h2>
          {list ? (
            <span className="text-meta text-muted-foreground">
              {crewCount(list.people.length, list.invites.length)}
            </span>
          ) : null}
        </div>
        {onInvite ? <InviteButton onClick={onInvite} /> : null}
      </div>
      {people.error ? <p className="text-sm text-destructive">{errorHint(people.error)}</p> : null}
      {list ? <CrewList me={me} list={list} onRemove={onRemove} onToast={onToast} /> : null}
      <p className="text-meta text-muted-foreground">
        Members can do everything {onInvite ? "you" : "the owner"} can, except change the AI, the
        plan, and who's here.
      </p>
    </section>
  );
}

function CrewList({
  me,
  list,
  onRemove,
  onToast,
}: {
  me: Me;
  list: PeopleList;
  onRemove: (person: Person) => void;
  onToast: (toast: ToastMessage) => void;
}) {
  const owner = me.user.role === "owner";
  const now = new Date();
  const tz = me.workspace.timezone;
  return (
    <div className="flex flex-col rounded-panel border border-border bg-card">
      {list.people.map((p) => (
        <PersonRow
          key={p.id}
          person={p}
          you={p.id === me.user.id}
          owner={owner}
          seen={lastSeen(p.last_seen_at, now, tz)}
          onRemove={() => onRemove(p)}
        />
      ))}
      {list.invites.map((i) => (
        <InviteRow
          key={i.id}
          invite={i}
          owner={owner}
          by={invitedBy(i, me.user.id)}
          state={inviteState(i, now, tz)}
          onToast={onToast}
        />
      ))}
    </div>
  );
}

const cell = "shrink-0 text-meta";

function PersonRow({
  person: p,
  you,
  owner,
  seen,
  onRemove,
}: {
  person: Person;
  you: boolean;
  owner: boolean;
  seen: string;
  onRemove: () => void;
}) {
  const role = p.role === "owner" ? "Owner" : "Member";
  const apps = appsLine(p);
  const menu =
    owner && !you ? (
      <RowMenu label={`More for ${p.name}`}>
        <DropdownMenuItem variant="destructive" onClick={onRemove} data-person-action="remove">
          Remove from the crew
        </DropdownMenuItem>
      </RowMenu>
    ) : null;
  return (
    <div
      data-person={p.id}
      className="flex items-center gap-3 border-t border-border px-4 py-3.5 first:border-t-0 md:gap-4 md:px-5 md:py-4"
    >
      <PersonAvatar initials={p.initials} tint={personTint(p.tint)} size={32} />
      <div className="flex min-w-0 grow basis-0 flex-col gap-0.5">
        <div className="flex items-baseline gap-1.5">
          <span className="truncate text-body leading-5 font-semibold text-foreground">
            {p.name}
          </span>
          {you ? <span className="text-meta text-hint">You</span> : null}
        </div>
        <span className="hidden truncate text-meta text-muted-foreground md:block">{p.email}</span>
        <span className="truncate text-meta text-muted-foreground md:hidden">
          {[role, apps, you ? null : seen].filter(Boolean).join(" · ")}
        </span>
      </div>
      <span className={cn(cell, "hidden w-27.5 font-medium text-copy md:block")}>{role}</span>
      <span
        className={cn(
          cell,
          "hidden w-47.5 truncate md:block",
          p.apps.length > 0 ? "text-muted-foreground" : "text-hint",
        )}
      >
        {apps}
      </span>
      <span className={cn(cell, "hidden w-32.5 text-muted-foreground md:block")}>{seen}</span>
      <div className="flex w-8 shrink-0 justify-end md:w-28">{menu}</div>
    </div>
  );
}

function InviteRow({
  invite: i,
  owner,
  by,
  state,
  onToast,
}: {
  invite: Invite;
  owner: boolean;
  by: string;
  state: InviteState;
  onToast: (t: ToastMessage) => void;
}) {
  const label = <InviteLabel state={state} />;
  const menu = owner ? <InviteMenu invite={i} onToast={onToast} /> : null;
  const tone = state.failed ? "text-destructive" : "text-muted-foreground";
  return (
    <div
      data-invite={i.id}
      data-invite-sent={i.sent_at ? "yes" : "no"}
      className="flex flex-col gap-3 border-t border-border px-4 py-3.5 first:border-t-0 md:flex-row md:items-center md:gap-4 md:px-5 md:py-4"
    >
      <div className="flex min-w-0 grow basis-0 items-center gap-3 md:gap-4">
        <span className="flex size-8 shrink-0 items-center justify-center rounded-full border border-dashed border-input">
          <Mail className="size-[15px] text-hint" strokeWidth={1.75} />
        </span>
        <div className="flex min-w-0 grow basis-0 flex-col gap-0.5">
          <span className="truncate text-body leading-5 font-semibold text-foreground">
            {i.email}
          </span>
          <span className="hidden text-meta text-muted-foreground md:block">{by}</span>
          <span className="flex items-center gap-1.5 text-meta md:hidden">
            {label}
            <span className={tone}>· {state.short}</span>
          </span>
        </div>
        <div className="w-8 shrink-0 md:hidden">{menu}</div>
      </div>
      <div className="hidden w-27.5 shrink-0 md:block">{label}</div>
      <span className={cn(cell, "hidden w-47.5 md:block", tone)}>{state.detail}</span>
      <span className="hidden w-32.5 shrink-0 md:block" />
      <div className="hidden w-28 shrink-0 items-center justify-end gap-1 md:flex">
        {owner ? <ResendButton invite={i} label="Resend" onToast={onToast} /> : null}
        {menu}
      </div>
      {owner ? (
        <div className="flex pl-11 md:hidden">
          <ResendButton invite={i} label="Resend invite" onToast={onToast} />
        </div>
      ) : null}
    </div>
  );
}

/** "Invited" in copper, or "Expired". */
function InviteLabel({ state }: { state: InviteState }) {
  const live = state.label === "Invited";
  return (
    <span className="flex items-center gap-1.5">
      <span className={cn("size-1.5 shrink-0 rounded-full", live ? "bg-copper" : "bg-hint")} />
      <span className={cn("text-meta font-medium", live ? "text-copper" : "text-muted-foreground")}>
        {state.label}
      </span>
    </span>
  );
}

function ResendButton({
  invite,
  label,
  onToast,
}: {
  invite: Invite;
  label: string;
  onToast: (t: ToastMessage) => void;
}) {
  const resend = useResend();
  return (
    <Button
      variant="outline"
      size="sm"
      className="h-8 border-input bg-card px-3 text-meta"
      disabled={resend.isPending}
      onClick={() =>
        resend.mutate(invite.id, {
          onSuccess: (out) => onToast(sentToast(out, true)),
          onError: (error) => onToast(errorToast(error)),
        })
      }
      data-invite-action="resend"
    >
      {label}
    </Button>
  );
}

function InviteMenu({ invite, onToast }: { invite: Invite; onToast: (t: ToastMessage) => void }) {
  const cancel = useCancelInvite();
  return (
    <RowMenu label={`More for the invite to ${invite.email}`}>
      <DropdownMenuItem
        variant="destructive"
        disabled={cancel.isPending}
        onClick={() => cancel.mutate(invite.id, { onError: (error) => onToast(errorToast(error)) })}
        data-invite-action="cancel"
      >
        Cancel invite
      </DropdownMenuItem>
    </RowMenu>
  );
}

function RowMenu({ label, children }: { label: string; children: ReactNode }) {
  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        render={
          <Button
            variant="ghost"
            size="icon"
            aria-label={label}
            className="text-muted-foreground"
          />
        }
      >
        <Ellipsis />
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="min-w-48">
        {children}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

/** Under the list (§6.1): the three notes. */
function HowItWorks() {
  const notes = [
    [
      "Everyone has their own chats",
      "Each person asks in threads of their own. Your Chief of Staff knows who asked, and keeps notes on each of you.",
    ],
    [
      "Everyone sees everything",
      "Chats, tasks, docs and decisions belong to the crew. Mine, in Chats, shows the ones you started.",
    ],
    [
      "Your own apps, and the shared ones",
      "Each person connects their own mail and calendar. Share an account with the crew from Integrations.",
    ],
  ];
  return (
    <section className="flex max-w-letter flex-col gap-4.5 pt-2 md:gap-5.5 md:pt-6">
      <h2 className="font-display text-[32px] leading-9.5 tracking-tight text-foreground">
        One Chief of Staff, who knows who's asking.
      </h2>
      {notes.map(([title, body], n) => (
        <div key={title} className="flex gap-5">
          <span className="w-4 shrink-0 font-mono text-meta leading-5.5 text-copper">
            {String(n + 1).padStart(2, "0")}
          </span>
          <div className="flex flex-col gap-1">
            <span className="text-body leading-5.5 font-semibold text-foreground">{title}</span>
            <span className="text-sm leading-5.25 text-copy">{body}</span>
          </div>
        </div>
      ))}
    </section>
  );
}

/** Remove from the crew (artboard 75): what happens, then Remove <name>. */
function RemoveDialog({ person, onClose }: { person: Person; onClose: () => void }) {
  const remove = useRemovePerson();
  const name = firstName(person.name);
  const apps =
    person.apps.length > 0
      ? `Their apps are disconnected, ${person.apps.join(", ")} with them. Your Chief of Staff can't use those any more.`
      : "Their apps are disconnected, shared ones included. Your Chief of Staff can't use those any more.";
  const close = (open: boolean) => {
    if (!open && !remove.isPending) onClose();
  };
  return (
    <Dialog open onOpenChange={close}>
      <DialogContent
        showCloseButton={false}
        className="gap-0 p-0 sm:max-w-120"
        data-remove-person={person.id}
      >
        <div className="flex flex-col gap-1.5 px-6 pt-6 pb-1">
          <DialogTitle className="font-display text-[28px] leading-8 font-normal tracking-tight text-foreground">
            Remove {name} from the crew?
          </DialogTitle>
          <DialogDescription className="text-sm leading-5 text-muted-foreground">
            {name} is signed out everywhere and can't come back unless you invite them again.
          </DialogDescription>
          <ul className="flex flex-col gap-2.5 pt-4 pb-5">
            {(
              [
                ["bg-moss", "Their chats, tasks and docs stay with the crew, under their name."],
                ["bg-destructive", apps],
                ["bg-destructive", "Their iMessage link is unlinked."],
              ] as const
            ).map(([dot, text]) => (
              <li key={text} className="flex items-start gap-2.5">
                <span className={cn("mt-1.75 size-1.5 shrink-0 rounded-full", dot)} />
                <span className="text-sm leading-5 text-copy">{text}</span>
              </li>
            ))}
          </ul>
          {remove.error ? (
            <p className="pb-4 text-sm text-destructive">{errorHint(remove.error)}</p>
          ) : null}
        </div>
        <div className="flex items-center justify-end gap-2 border-t border-border px-6 pt-4 pb-5">
          <Button
            variant="outline"
            size="lg"
            className="border-input bg-card px-3.5"
            onClick={onClose}
            disabled={remove.isPending}
          >
            Cancel
          </Button>
          <Button
            size="lg"
            className="bg-destructive px-3.5 text-white hover:bg-destructive/90"
            disabled={remove.isPending}
            onClick={() => remove.mutate(person.id, { onSuccess: onClose })}
            data-person-action="confirm-remove"
          >
            {remove.isPending ? "Removing…" : `Remove ${name}`}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
